import {
  Path,
  Element,
  Transforms,
  Editor,
  Node,
  Text,
} from 'domains/reporter/RichTextEditor/core';
import type { CreateEnhanceEditorState } from '../../types';
import { type HeadingErrorPluginPropertyOptions } from './types';
import { HeadingLevel } from '../heading/constants';
import { HEADING_PLUGIN_ID } from '../heading/types';
import { doesH1HeadingMatchText, isH1HeadingNode, normalizeText } from './utils';
import type { NodeEntry } from 'slate';

// Checks to see if there exists a node in the range with
const matchFunction = (n: Node, headingText: string): boolean => {
  return (
    doesH1HeadingMatchText(n, headingText) &&
    !Array.from(Node.texts(n)).some(
      (textNodeEntry) =>
        // TODO headingError is added as a mark but not defined as a property for the heading plugin element
        (textNodeEntry[0] as any).headingError === true
    )
  );
};

const hasHeadingErrorMark = (node: Node): boolean => {
  return Array.from(Node.texts(node)).some(
    // TODO headingError is added as a mark but not defined as a property for the heading plugin element
    (textNodeEntry) => (textNodeEntry[0] as any).headingError === true
  );
};

const clearHeadingError = (editor: Editor, path: Path): void => {
  Transforms.unsetNodes(editor, 'headingError', {
    at: path,
    match: (node: Node): node is Text => Text.isText(node),
    split: true,
  });
};

const setHeadingError = (editor: Editor, path: Path, value: boolean): void => {
  if (!value) {
    clearHeadingError(editor, path);
    return;
  }

  const { selection: originalSelection } = editor;
  if (originalSelection == null) {
    return;
  }

  const headingRange = Editor.range(editor, path);
  Transforms.select(editor, headingRange);
  editor.addMark('headingError', value);

  Transforms.select(editor, originalSelection);
};

const clearStaleHeadingErrors = (editor: Editor): void => {
  const headingEntriesWithError = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (node: Node) => isH1HeadingNode(node) && hasHeadingErrorMark(node),
    })
  );

  for (const [node, path] of headingEntriesWithError) {
    const headingText = normalizeText(Node.string(node));
    if (headingText === '') {
      clearHeadingError(editor, path);
      continue;
    }

    const hasDuplicateHeading =
      Array.from(
        Editor.nodes(editor, {
          at: [],
          match: (n: Node) => doesH1HeadingMatchText(n, headingText),
        })
      ).length > 1;

    if (!hasDuplicateHeading) {
      clearHeadingError(editor, path);
    }
  }
};

// This plugin owns headingError marks for inline rendering only (red text/tooltip).
// Submit/save gating is derived from content in useSyncHeadingErrorState.
//
// This will flag when there is a duplicate heading in the editor. For a given heading node, we first check to see
// if there is at least one heading node before it that contains the same text and is not itself a duplicate header.
// If there is, then we flag it. Then, we do the same check for after the heading. This way, we are only flagging
// duplicate headers and not the original header.
export const enhanceEditorStateHeadingError: CreateEnhanceEditorState<
  HeadingErrorPluginPropertyOptions
> =
  ({ pluginID }) =>
  (editor: Editor) => {
    const { normalizeNode } = editor;
    let isClearingStaleHeadingErrors = false;

    editor.normalizeNode = (entry: NodeEntry) => {
      const [node, path] = entry;

      if (
        Element.isElement(node) &&
        node.type === HEADING_PLUGIN_ID &&
        Node.string(node) !== '' &&
        node.level === HeadingLevel.H1
      ) {
        const startOfEditor = Editor.start(editor, []);
        const headingText = normalizeText(Node.string(node));
        const beforeHeadingPoint = Editor.before(
          editor,
          Editor.point(editor, path, { edge: 'start' })
        );

        const previousMatchingHeadings =
          beforeHeadingPoint != null
            ? Array.from(
                Editor.nodes(editor, {
                  at: Editor.range(editor, startOfEditor, beforeHeadingPoint),
                  match: (n: Node) => matchFunction(n, headingText),
                })
              )
            : [];

        if (previousMatchingHeadings.length > 0) {
          setHeadingError(editor, path, true);
        } else {
          const afterHeadingPoint = Editor.after(
            editor,
            Editor.point(editor, path, { edge: 'end' })
          );
          const endOfEditor = Editor.end(editor, []);

          const afterMatchingHeadings =
            afterHeadingPoint != null
              ? Array.from(
                  Editor.nodes(editor, {
                    at: Editor.range(editor, afterHeadingPoint, endOfEditor),
                    match: (n: Node) => matchFunction(n, headingText),
                  })
                )
              : [];

          if (afterMatchingHeadings.length > 0) {
            setHeadingError(editor, path, true);
          } else if (hasHeadingErrorMark(node)) {
            setHeadingError(editor, path, false);
          }
        }
      }

      if (
        !isClearingStaleHeadingErrors &&
        Element.isElement(node) &&
        node.type === HEADING_PLUGIN_ID
      ) {
        isClearingStaleHeadingErrors = true;
        try {
          clearStaleHeadingErrors(editor);
        } finally {
          isClearingStaleHeadingErrors = false;
        }
      }

      if (
        Element.isElement(node) &&
        node.type === HEADING_PLUGIN_ID &&
        node.level !== HeadingLevel.H1 &&
        hasHeadingErrorMark(node)
      ) {
        clearHeadingError(editor, path);
      }
      normalizeNode(entry);
    };

    return editor;
  };
